import { useCallback, useState, useMemo } from 'react'
import { Currency, Trade, TradeType } from '@pancakeswap/sdk'
import { Button, Box, Text, AutoRenewIcon } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { ApprovalState, useApproveCallbackFromQOMXTrade } from 'hooks/useApproveCallback'
import { useQOMXSwapCallback } from 'hooks/useQOMXSwapCallback'
import { Field } from 'state/swap/actions'
import { AutoRow } from 'components/Layout/Row'
import { AutoColumn } from 'components/Layout/Column'
import ConnectWalletButton from 'components/ConnectWalletButton'

interface QOMXSwapCommitButtonProps {
  account?: string | null
  trade: Trade<Currency, Currency, TradeType> | null
  swapInputError?: string
  allowedSlippage: number
  recipient?: string | null
  inputIsNative?: boolean
  outputIsNative?: boolean
}

export default function QOMXSwapCommitButton({
  account,
  trade,
  swapInputError,
  allowedSlippage,
  recipient,
  inputIsNative = false,
  outputIsNative = false,
}: QOMXSwapCommitButtonProps) {
  const { t } = useTranslation()
  // Skip approval flow if input is native (QOM)
  const tradeForApproval = inputIsNative ? undefined : trade
  const [approval, approveCallback] = useApproveCallbackFromQOMXTrade(tradeForApproval ?? undefined, allowedSlippage)
  const { callback: swapCallback, error: swapError } = useQOMXSwapCallback(
    trade,
    allowedSlippage,
    recipient,
    inputIsNative,
    outputIsNative,
  )
  const [pendingApproval, setPendingApproval] = useState(false)
  const [pendingSwap, setPendingSwap] = useState(false)

  const showApproveFlow = useMemo(
    () =>
      !inputIsNative &&
      !swapInputError &&
      (approval === ApprovalState.NOT_APPROVED || approval === ApprovalState.PENDING),
    [approval, swapInputError, inputIsNative],
  )

  const handleApprove = useCallback(async () => {
    setPendingApproval(true)
    try {
      await approveCallback()
    } finally {
      setPendingApproval(false)
    }
  }, [approveCallback])

  const handleSwap = useCallback(async () => {
    if (!swapCallback) return
    setPendingSwap(true)
    try {
      await swapCallback()
    } finally {
      setPendingSwap(false)
    }
  }, [swapCallback])

  if (!account) {
    return <ConnectWalletButton width="100%" />
  }

  if (swapInputError) {
    return (
      <Button width="100%" disabled>
        {swapInputError}
      </Button>
    )
  }

  if (!trade) {
    return (
      <Button width="100%" disabled>
        {t('Enter an amount')}
      </Button>
    )
  }

  return (
    <AutoColumn gap="md">
      <Box mb="8px" p="12px" style={{ background: 'rgba(31, 199, 212, 0.1)', borderRadius: '8px', border: '1px solid rgba(31, 199, 212, 0.3)' }}>
        <Text fontSize="12px" color="#1FC7D4" fontWeight="600">
          {t('Routing through QOMX DEX')}
        </Text>
      </Box>
      {showApproveFlow ? (
        <AutoRow style={{ gap: '8px' }}>
          <Button
            onClick={handleApprove}
            disabled={approval !== ApprovalState.NOT_APPROVED || pendingApproval}
            width="48%"
            isLoading={pendingApproval || approval === ApprovalState.PENDING}
            endIcon={pendingApproval || approval === ApprovalState.PENDING ? <AutoRenewIcon spin color="currentColor" /> : null}
          >
            {approval === ApprovalState.APPROVED
              ? t('Approved')
              : t('Approve %asset%', { asset: trade.inputAmount.currency.symbol })}
          </Button>
          <Button width="48%" disabled={approval !== ApprovalState.APPROVED || !swapCallback || pendingSwap} onClick={handleSwap}>
            {t('Swap')}
          </Button>
        </AutoRow>
      ) : (
        <Button
          width="100%"
          onClick={handleSwap}
          disabled={!swapCallback || !!swapError || pendingSwap}
          isLoading={pendingSwap}
          endIcon={pendingSwap ? <AutoRenewIcon spin color="currentColor" /> : null}
        >
          {swapError || t('Swap via QOMX')}
        </Button>
      )}
    </AutoColumn>
  )
}
